import { AppConfigId, AppIcons, AppIdentifier } from '@aipb/gulliver/env/model';
import { APP_NAME } from '@aipb/gulliver/env/util';
import {
  AppHistory,
  DynamicRouteItem,
  FRAME_LOCAL_STORAGE_KEY,
  FrameAction,
  FrameApplication,
  RouteCategory,
  RouteCategoryItem,
  RouteItem,
  StaticRouteItem
} from '@aipb/gulliver/frame/model';
import { baseUrl } from '@aipb/shared/util/routing';
import { BreakpointObserver } from '@angular/cdk/layout';
import { inject, Injectable } from '@angular/core';
import { createStore, select, Store, withProps } from '@ngneat/elf';
import { excludeKeys, localStorageStrategy, persistState } from '@ngneat/elf-persist-state';
import { combineLatest, mergeMap, Observable, of, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';

interface FrameProps {
  loading: boolean;
  appIcons: AppIcons;
  applications: FrameApplication[];
  version: string;
  forceFullHeight: boolean;
  isMobile: boolean;
  routeCategories: RouteCategory[];
  staticRoutes: StaticRouteItem[];
  actions: FrameAction[];
  activeApp: AppIdentifier;
  configIds: Record<AppIdentifier, string>;
  appHistory: {
    [app in AppIdentifier]?: {
      [configId in AppConfigId]?: AppHistory;
    };
  };
  sidebarExpanded: boolean;
  dynamicRoutes: {
    [app in AppIdentifier]?: {
      [configId in AppConfigId]?: DynamicRouteItem[];
    };
  };
}

export const MOBILE_MEDIA_QUERY = '(max-width: 900px)';

@Injectable({
  providedIn: 'root'
})
export class FrameRepository {
  bpo = inject(BreakpointObserver);
  applications$: Observable<FrameApplication[]>;
  sidebarExpanded$: Observable<boolean>;
  version$: Observable<string>;
  actions$: Observable<FrameAction[]>;
  loading$: Observable<boolean>;
  forceFullHeight$: Observable<boolean>;
  routeCategories$: Observable<RouteCategory[]>;
  dynamicRouteCategoryItems$: Observable<RouteCategoryItem[]>;
  activeApp$: Observable<AppIdentifier>;
  activeApplication$: Observable<FrameApplication>;
  isMobile$: Observable<boolean>;
  staticRoutes$: Observable<StaticRouteItem[]>;
  configIds$: Observable<Record<AppIdentifier, string>>;
  private appName = inject(APP_NAME);
  private readonly frameStore: Store<never, FrameProps>;

  constructor() {
    this.frameStore = createStore(
      { name: 'frame' },
      withProps<FrameProps>({
        loading: false,
        appIcons: {},
        applications: [],
        routeCategories: [],
        version: '',
        staticRoutes: [],
        actions: [],
        forceFullHeight: false,
        activeApp: null,
        configIds: {
          fulltext: 'default',
          gorazd: 'default',
          lana: 'default',
          merc: 'default',
          catalog: 'default',
          explorer: 'default',
          dashboard: 'default',
          'keycloak-manager': 'default',
          'report-tester': 'default',
          'config-viewer': 'default',
          browser: 'default',
          'personal-content': 'default',
          'dynamic-gallery': 'default',
          'browser-mirador': 'default',
          'lana-tester': 'default',
          'customer-config-editor': 'default',
          'zip-manager': 'default'
        },
        isMobile: this.bpo.isMatched(MOBILE_MEDIA_QUERY),
        appHistory: {},
        sidebarExpanded: true,
        dynamicRoutes: {}
      })
    );

    persistState(this.frameStore, {
      key: `${this.appName}-${FRAME_LOCAL_STORAGE_KEY}`,
      storage: localStorageStrategy,
      source: () =>
        this.frameStore.pipe(
          excludeKeys([
            'loading',
            'appIcons',
            'applications',
            'version',
            'routeCategories',
            'staticRoutes',
            'actions',
            'forceFullHeight',
            'activeApp',
            'configIds',
            'isMobile',
            'appHistory'
          ])
        )
    });

    this.applications$ = this.frameStore.pipe(select((state) => state.applications));
    this.sidebarExpanded$ = this.frameStore.pipe(select((state) => state.sidebarExpanded));
    this.version$ = this.frameStore.pipe(select((state) => state.version));
    this.actions$ = this.frameStore.pipe(select((state) => state.actions));
    this.loading$ = this.frameStore.pipe(select((state) => state.loading));
    this.forceFullHeight$ = this.frameStore.pipe(select((state) => state.forceFullHeight));
    this.routeCategories$ = this.frameStore.pipe(select((state) => state.routeCategories));
    this.activeApp$ = this.frameStore.pipe(select((state) => state.activeApp));
    this.configIds$ = this.frameStore.pipe(select((state) => state.configIds));
    this.dynamicRouteCategoryItems$ = combineLatest([
      this.routeCategories$,
      this.frameStore.pipe(select((state) => state.dynamicRoutes)),
      this.activeApp$
    ]).pipe(
      switchMap(([routeCategories]) => {
        if (routeCategories.length === 0) {
          return of([]);
        }

        return combineLatest(
          routeCategories.map((routeCategory) =>
            this.selectDynamicRoutesByCategory(routeCategory.id).pipe(
              map((items) => ({
                category: routeCategory,
                routes: items
              }))
            )
          )
        );
      }),
      map((routeCategoryItems) => routeCategoryItems.filter((rci) => rci.routes.length > 0))
    );
    this.activeApplication$ = this.activeApp$.pipe(
      mergeMap((activeApp) =>
        this.frameStore.pipe(
          select((state) => {
            return state.applications.find((app) => app.id === activeApp && app.configId === state.configIds[app.id]);
          })
        )
      )
    );
    this.isMobile$ = this.frameStore.pipe(select((state) => state.isMobile));
    this.staticRoutes$ = this.frameStore.pipe(select((state) => state.staticRoutes));
  }

  get applications() {
    return this.frameStore.getValue().applications;
  }

  get isMobile() {
    return this.frameStore.getValue().isMobile;
  }

  get activeApp(): AppIdentifier {
    return this.frameStore.getValue().activeApp;
  }

  getDynamicRouteByIndex(i: number) {
    const appId = this.frameStore.getValue().activeApp;
    const configId = this.getConfigId(appId);
    const dynamicRoutes = this.frameStore.getValue().dynamicRoutes;

    if (
      !(appId in dynamicRoutes) ||
      !(configId in dynamicRoutes[appId]) ||
      i >= dynamicRoutes[appId][configId].length
    ) {
      return null;
    }

    return dynamicRoutes[appId][configId][i];
  }

  getAppHistory(app: AppIdentifier, configId: AppConfigId): AppHistory {
    const appHistory = this.frameStore.getValue().appHistory;

    if (!(app in appHistory)) {
      return null;
    }

    if (!(configId in appHistory[app])) {
      return null;
    }

    return appHistory[app][configId];
  }

  updateAppHistory(app: AppIdentifier, url: string) {
    if (!app) return;

    const u = new URL(url, baseUrl());

    const searchParams = new URLSearchParams(u.search);

    const queryParams = {};
    searchParams.forEach((value, key) => {
      queryParams[key] = value;
    });

    const configId = this.getConfigId(app);

    this.frameStore.update((state) => {
      return {
        ...state,
        appHistory: {
          ...state.appHistory,
          [app]: {
            ...state.appHistory[app],
            [configId]: {
              url: decodeURIComponent(u.pathname),
              queryParams: queryParams
            }
          }
        }
      };
    });
  }

  getConfigId(app: AppIdentifier): string {
    return this.frameStore.getValue().configIds[app];
  }

  hasApplication(application: AppIdentifier): boolean {
    return this.applications.some((app) => app.id === application);
  }

  selectDynamicRoutesByCategory(id: string): Observable<DynamicRouteItem[]> {
    const appId = this.frameStore.getValue().activeApp;
    const configId = this.getConfigId(appId);

    return this.frameStore.pipe(
      select((state) => {
        let dynamicRoutes = [];

        if (appId in state.dynamicRoutes && configId in state.dynamicRoutes[appId]) {
          dynamicRoutes = state.dynamicRoutes[appId][configId].filter((r) => r.category === id);
        }

        return dynamicRoutes;
      })
    );
  }

  setStaticRoutes(staticRoutes: RouteItem[]) {
    this.frameStore.update((state) => ({
      ...state,
      staticRoutes
    }));
  }

  setActions(actions: FrameAction[]) {
    this.frameStore.update((state) => ({
      ...state,
      actions
    }));
  }

  setRouteCategories(routeCategories: RouteCategory[]) {
    this.frameStore.update((state) => ({
      ...state,
      routeCategories
    }));
  }

  hasDynamicRoute(url: string): boolean {
    const appId = this.frameStore.getValue().activeApp;
    const configId = this.getConfigId(appId);
    const dynamicRoutes = this.frameStore.getValue().dynamicRoutes;

    if (!(appId in dynamicRoutes) || !(configId in dynamicRoutes[appId])) {
      return false;
    }

    return dynamicRoutes[appId][configId].some((route) => route.url === url);
  }

  addDynamicRoute(routeItem: RouteItem) {
    const appId = this.frameStore.getValue().activeApp;
    const configId = this.getConfigId(appId);

    if (
      !(appId in this.frameStore.getValue().dynamicRoutes) ||
      !(configId in this.frameStore.getValue().dynamicRoutes[appId])
    ) {
      this.frameStore.update((state) => ({
        ...state,
        dynamicRoutes: {
          ...state.dynamicRoutes,
          [appId]: {
            ...state.dynamicRoutes[appId],
            [configId]: []
          }
        }
      }));
    }

    if (this.frameStore.getValue().dynamicRoutes[appId][configId].some((r) => r.url === routeItem.url)) {
      return;
    }

    this.frameStore.update((state) => ({
      ...state,
      dynamicRoutes: {
        ...state.dynamicRoutes,
        [appId]: {
          ...state.dynamicRoutes[appId],
          [configId]: [
            ...state.dynamicRoutes[appId][configId],
            {
              deletable: true,
              ...routeItem
            }
          ]
        }
      }
    }));
  }

  setApplications(applications: FrameApplication[]) {
    this.frameStore.update((state) => ({
      ...state,
      applications
    }));
  }

  setAppIcons(appIcons: AppIcons) {
    this.frameStore.update((state) => ({
      ...state,
      appIcons
    }));
  }

  setVersion(version: string) {
    this.frameStore.update((state) => ({
      ...state,
      version
    }));
  }

  setLoading(loading: boolean) {
    this.frameStore.update((state) => ({
      ...state,
      loading
    }));
  }

  setForceFullHeight(forceFullHeight: boolean) {
    this.frameStore.update((state) => ({
      ...state,
      forceFullHeight
    }));
  }

  setSidebarExpanded(sidebarExpanded: boolean) {
    this.frameStore.update((state) => ({
      ...state,
      sidebarExpanded
    }));
  }

  toggleSidebarExpanded() {
    this.frameStore.update((state) => ({
      ...state,
      sidebarExpanded: !state.sidebarExpanded
    }));
  }

  setConfigId(app: AppIdentifier, configId: string) {
    this.frameStore.update((state) => ({
      ...state,
      configIds: {
        ...state.configIds,
        [app]: configId
      }
    }));
  }

  setIsMobile(isMobile: boolean) {
    this.frameStore.update((state) => ({
      ...state,
      isMobile
    }));
  }

  setActiveApp(activeApp: AppIdentifier) {
    this.frameStore.update((state) => ({
      ...state,
      activeApp
    }));
  }

  setDynamicRoutes(items: DynamicRouteItem[]) {
    const appId = this.frameStore.getValue().activeApp;
    const configId = this.getConfigId(appId);

    this.frameStore.update((state) => ({
      ...state,
      dynamicRoutes: {
        ...state.dynamicRoutes,
        [appId]: {
          ...state.dynamicRoutes[appId],
          [configId]: items
        }
      }
    }));
  }

  removeDynamicRoute(routeItem: RouteItem) {
    const appId = this.frameStore.getValue().activeApp;
    const configId = this.getConfigId(appId);

    this.frameStore.update((state) => ({
      ...state,
      dynamicRoutes: {
        ...state.dynamicRoutes,
        [appId]: {
          ...state.dynamicRoutes[appId],
          [configId]: state.dynamicRoutes[appId][configId].filter((r) => r.url !== routeItem.url)
        }
      }
    }));
  }

  getDynamicRouteIndex(routeItem: RouteItem): number {
    const appId = this.frameStore.getValue().activeApp;
    const configId = this.getConfigId(appId);

    if (
      !(appId in this.frameStore.getValue().dynamicRoutes) ||
      !(configId in this.frameStore.getValue().dynamicRoutes[appId])
    ) {
      return -1;
    }

    return this.frameStore.getValue().dynamicRoutes[appId][configId].findIndex((r) => r === routeItem);
  }
}
